import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAdminApi } from '../hooks/useAdminApi';
import {
  ArrowLeft,
  User,
  MapPin,
  Phone,
  Clock,
  FileText,
  Calendar,
  Truck,
  CheckCircle,
  AlertTriangle,
  Navigation,
  Package,
  Coffee,
  Eye,
  EyeOff,
  Printer,
  Share2,
  X,
  Download,
  Mail,
  Copy,
  ExternalLink,
  FileImage,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  RefreshCw,
  Archive,
  ArchiveRestore,
  Trash2,
  Edit,
  Flag,
  Route as RouteIcon,
} from 'lucide-react';
import { formatStopDateTime, formatShortDate } from '../../lib/dateFormat';
import ReportGpsMapModal from '../../components/ReportGpsMapModal';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { roundToQuarterHour, formatHoursDecimal, formatHoursHuman } from '../../lib/hoursFormat';
import AdminReportEditModal from '../components/AdminReportEditModal';
import { DriverAssignmentChip } from '../components/DriverAssignmentChip';

const DriverDetailPage = () => {
  const { driverId } = useParams();
  const { get, patch, del: apiDelete } = useAdminApi();
  const [driver, setDriver] = useState(null);
  const [reports, setReports] = useState([]);
  const [archivedReports, setArchivedReports] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('position');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [allDocuments, setAllDocuments] = useState([]);
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [documentZoom, setDocumentZoom] = useState(100);
  const [confirmAction, setConfirmAction] = useState(null); // {type, report}

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      // Get driver info
      const driversRes = await get('/saas/dashboard/drivers');
      if (driversRes.success) {
        const found = driversRes.data.find(d => d.id === driverId);
        setDriver(found);
      }
      
      // Get driver reports
      const reportsRes = await get(`/saas/driver/${driverId}/reports`);
      if (reportsRes.success) {
        setReports(reportsRes.data);
      }
      
      setLoading(false);
    };
    
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId]);

  const fetchArchivedReports = async () => {
    const res = await get(`/saas/driver/${driverId}/reports/archived`);
    if (res.success) setArchivedReports(res.data);
  };

  const handleArchive = async (report) => {
    const res = await patch(`/saas/driver/${driverId}/reports/${report.id}/archive`);
    if (res.success) {
      setReports(prev => prev.filter(r => r.id !== report.id));
      setArchivedReports(prev => [...prev, { ...report, archived: true }]);
      toast.success(`Rapport ${report.report_number} archivé`);
      setConfirmAction(null);
    } else {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleUnarchive = async (report) => {
    const res = await patch(`/saas/driver/${driverId}/reports/${report.id}/unarchive`);
    if (res.success) {
      setArchivedReports(prev => prev.filter(r => r.id !== report.id));
      setReports(prev => [...prev, { ...report, archived: false }].sort((a, b) => {
        const dateCmp = (b.date || '').localeCompare(a.date || '');
        if (dateCmp !== 0) return dateCmp;
        const tCmp = (b.start_time || '').localeCompare(a.start_time || '');
        if (tCmp !== 0) return tCmp;
        return (b.created_at || '').localeCompare(a.created_at || '');
      }));
      toast.success(`Rapport ${report.report_number} désarchivé`);
    } else {
      toast.error('Erreur lors du désarchivage');
    }
  };

  const handlePermanentDelete = async (report) => {
    const res = await apiDelete(`/saas/driver/${driverId}/reports/${report.id}/permanent`);
    if (res.success) {
      setReports(prev => prev.filter(r => r.id !== report.id));
      setArchivedReports(prev => prev.filter(r => r.id !== report.id));
      toast.success(`Rapport ${report.report_number} supprimé définitivement`);
      setConfirmAction(null);
    } else {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Check if report has linked invoices
  const reportHasInvoice = (report) => {
    // Simple heuristic: completed reports that were billed
    return report.invoiced || report.invoice_id;
  };

  const askConfirmation = (type, report) => {
    if (reportHasInvoice(report)) {
      // Already invoiced - no warning needed
      if (type === 'archive') handleArchive(report);
      else if (type === 'delete') setConfirmAction({ type, report });
    } else {
      setConfirmAction({ type, report });
    }
  };

  const openReport = (report) => {
    setSelectedReport(report);
    setShowReportModal(true);
  };

  const printReport = () => {
    // Open print page in new tab
    const printUrl = `/admin/print-report?driver=${driverId}&report=${encodeURIComponent(selectedReport?.report_number)}`;
    window.open(printUrl, '_blank');
    toast.success('Page d\'impression ouverte dans un nouvel onglet');
  };

  const printReportDirect = () => {
    // Direct print without preview
    const printContent = generatePrintContent();
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const generatePrintContent = () => {
    const report = selectedReport;
    const allDocs = getAllReportDocuments(report);
    const hours = calculateHours(report);
    
    // Generate stops HTML
    const stopsHtml = report?.stops?.sort((a, b) => a.order - b.order).map((stop, index) => {
      const stopDocs = stop.scanned_documents || [];
      return `
        <div style="border: 1px solid #ddd; border-radius: 8px; padding: 16px; margin-bottom: 12px; page-break-inside: avoid;">
          <div style="display: flex; align-items: flex-start; gap: 12px;">
            <div style="width: 32px; height: 32px; border-radius: 50%; background: ${stop.stop_type === 'pickup' ? '#3b82f6' : stop.stop_type === 'pause' ? '#f97316' : '#22c55e'}; color: white; display: flex; align-items: center; justify-content: center; font-weight: bold; flex-shrink: 0;">
              ${index + 1}
            </div>
            <div style="flex: 1;">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
                <strong style="font-size: 16px;">${stop.client_name}</strong>
                <span style="font-size: 12px; padding: 2px 8px; border-radius: 4px; background: ${stop.stop_type === 'pickup' ? '#dbeafe' : stop.stop_type === 'pause' ? '#ffedd5' : '#dcfce7'}; color: ${stop.stop_type === 'pickup' ? '#1d4ed8' : stop.stop_type === 'pause' ? '#c2410c' : '#15803d'};">
                  ${stop.stop_type === 'pickup' ? 'Cueillette' : stop.stop_type === 'pause' ? 'Pause' : 'Livraison'}
                </span>
              </div>
              ${stop.address ? `<p style="color: #666; margin: 4px 0; font-size: 14px;">📍 ${stop.address}</p>` : ''}
              ${stop.stop_type === 'pause' && stop.pause_type ? `<p style="color: #f97316; font-size: 14px;">${stop.pause_type === 'lunch' ? 'Dîner' : stop.pause_type === 'dinner' ? 'Souper' : stop.pause_type === 'break' ? 'Pause café' : 'Nuit / Repos'}</p>` : ''}
              ${stop.quantities ? `
                <div style="display: flex; gap: 8px; flex-wrap: wrap; margin: 8px 0;">
                  ${stop.quantities.palettes > 0 ? `<span style="background: #f3f4f6; padding: 4px 8px; border-radius: 4px; font-size: 12px;">${stop.quantities.palettes} palettes</span>` : ''}
                  ${stop.quantities.units > 0 ? `<span style="background: #f3f4f6; padding: 4px 8px; border-radius: 4px; font-size: 12px;">${stop.quantities.units} unités</span>` : ''}
                </div>
              ` : ''}
              ${stop.delivery_note_numbers?.length > 0 ? `<p style="font-size: 14px; color: #666;"><strong>Bons:</strong> ${stop.delivery_note_numbers.join(', ')}</p>` : ''}
              ${stop.notes ? `<p style="font-size: 13px; color: #92400e; background: #fffbeb; border-left: 3px solid #f59e0b; padding: 6px 10px; margin: 8px 0; white-space: pre-wrap;"><strong>Notes :</strong> ${String(stop.notes).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>` : ''}
              <div style="display: flex; gap: 16px; font-size: 12px; color: #888; margin-top: 8px;">
                ${stop.arrival_datetime ? `<span>⏰ Arrivée: ${new Date(stop.arrival_datetime).toLocaleString('fr-CA')}</span>` : ''}
                ${stop.departure_datetime ? `<span>Départ: ${new Date(stop.departure_datetime).toLocaleString('fr-CA')}</span>` : ''}
              </div>
              ${stopDocs.length > 0 ? `
                <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #eee;">
                  <p style="font-size: 12px; color: #666; margin-bottom: 8px;">📎 Documents scannés (${stopDocs.length}):</p>
                  <div style="display: flex; gap: 8px; flex-wrap: wrap;">
                    ${stopDocs.map(doc => `<span style="background: #fef3c7; color: #92400e; padding: 4px 8px; border-radius: 4px; font-size: 11px;">📄 ${doc.filename || 'Document'}</span>`).join('')}
                  </div>
                </div>
              ` : ''}
            </div>
          </div>
        </div>
      `;
    }).join('') || '<p style="color: #666; text-align: center; padding: 20px;">Aucun arrêt enregistré</p>';
    
    // Generate documents pages HTML
    const documentsHtml = allDocs.map((doc, index) => `
      <div style="page-break-before: always; padding: 20px;">
        <div style="background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 8px; padding: 16px; margin-bottom: 16px;">
          <h3 style="margin: 0 0 8px 0; color: #212529;">📄 Document ${index + 1}/${allDocs.length}</h3>
          <p style="margin: 0; color: #666; font-size: 14px;">
            <strong>${doc.filename || 'Document'}</strong> - ${getDocumentTypeLabel(doc.type)} - Arrêt: ${doc.stopName || 'N/A'}
          </p>
        </div>
        <div style="text-align: center; background: white; border: 1px solid #dee2e6; border-radius: 8px; padding: 20px; min-height: 600px; display: flex; align-items: center; justify-content: center;">
          ${doc.data || doc.image_url ? `<img src="${(() => { const url = doc.data || doc.image_url || ''; return url.startsWith('data:') ? url : (url ? process.env.REACT_APP_BACKEND_URL + url : ''); })()}" style="max-width: 100%; max-height: 700px; object-fit: contain;" alt="${doc.filename || 'Document'}" />` : '<p style="color: #999;">Aperçu non disponible</p>'}
        </div>
      </div>
    `).join('');
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Rapport ${report?.report_number} - ${driver?.first_name} ${driver?.last_name}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #212529; background: white; }
          @page { size: letter; margin: 0.5in; }
          @media print {
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          }
        </style>
      </head>
      <body>
        <!-- Page 1: Report Summary -->
        <div style="padding: 20px;">
          <!-- Header -->
          <div style="text-align: center; border-bottom: 2px solid #f59e0b; padding-bottom: 20px; margin-bottom: 20px;">
            <h1 style="color: #f59e0b; font-size: 28px; margin-bottom: 8px;">RAPPORT JOURNALIER</h1>
            <p style="font-size: 20px; font-weight: bold; color: #374151;">${report?.report_number || 'N/A'}</p>
          </div>
          
          <!-- Driver & Date -->
          <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; margin-bottom: 20px;">
            <div style="background: #f3f4f6; padding: 16px; border-radius: 8px;">
              <p style="color: #6b7280; font-size: 12px; text-transform: uppercase; margin-bottom: 4px;">Chauffeur</p>
              <p style="font-size: 18px; font-weight: bold;">${driver?.first_name} ${driver?.last_name}</p>
            </div>
            <div style="background: #f3f4f6; padding: 16px; border-radius: 8px;">
              <p style="color: #6b7280; font-size: 12px; text-transform: uppercase; margin-bottom: 4px;">Véhicule</p>
              <p style="font-size: 18px; font-weight: bold;">${report?.vehicle_label || '--'}</p>
            </div>
            <div style="background: #f3f4f6; padding: 16px; border-radius: 8px;">
              <p style="color: #6b7280; font-size: 12px; text-transform: uppercase; margin-bottom: 4px;">Date</p>
              <p style="font-size: 18px; font-weight: bold;">${report?.date || 'N/A'}</p>
            </div>
          </div>
          
          <!-- Hours Summary -->
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px;">
            <div style="background: #f3f4f6; padding: 12px; border-radius: 8px; text-align: center;">
              <p style="color: #6b7280; font-size: 11px; text-transform: uppercase;">Début</p>
              <p style="font-size: 20px; font-weight: bold;">${report?.start_time || '--:--'}</p>
            </div>
            <div style="background: #f3f4f6; padding: 12px; border-radius: 8px; text-align: center;">
              <p style="color: #6b7280; font-size: 11px; text-transform: uppercase;">Fin</p>
              <p style="font-size: 20px; font-weight: bold;">${report?.end_time || '--:--'}</p>
            </div>
            <div style="background: #fef3c7; border: 2px solid #f59e0b; padding: 12px; border-radius: 8px; text-align: center;">
              <p style="color: #92400e; font-size: 11px; text-transform: uppercase;">Heures payables</p>
              <p style="font-size: 20px; font-weight: bold; color: #f59e0b;">${formatHoursDecimal(hours.payableHours)}</p>
            </div>
            <div style="background: #f3f4f6; padding: 12px; border-radius: 8px; text-align: center;">
              <p style="color: #6b7280; font-size: 11px; text-transform: uppercase;">Distance</p>
              <p style="font-size: 20px; font-weight: bold;">${report?.total_km || '--'} km</p>
            </div>
          </div>
          
          <!-- Stops -->
          <div style="margin-bottom: 20px;">
            <h2 style="font-size: 18px; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 16px;">
              📍 Arrêts (${report?.stops?.length || 0})
            </h2>
            ${stopsHtml}
          </div>
          
          <!-- Documents Summary -->
          ${allDocs.length > 0 ? `
            <div style="background: #fef3c7; border: 1px solid #fcd34d; border-radius: 8px; padding: 16px; margin-bottom: 20px;">
              <h3 style="margin: 0 0 8px 0; color: #92400e;">📎 Documents scannés: ${allDocs.length}</h3>
              <p style="color: #a16207; font-size: 14px;">Les documents sont inclus dans les pages suivantes.</p>
            </div>
          ` : ''}
          
          <!-- Footer -->
          <div style="border-top: 1px solid #e5e7eb; padding-top: 16px; text-align: center; color: #6b7280; font-size: 12px;">
            <p>Rapport généré le ${new Date().toLocaleString('fr-CA')}</p>
            <p>Statut: ${report?.status === 'completed' ? 'TERMINÉ' : 'BROUILLON'}</p>
          </div>
        </div>
        
        <!-- Document Pages -->
        ${documentsHtml}
      </body>
      </html>
    `;
  };

  const copyReportLink = () => {
    const link = `${window.location.origin}/reports/${selectedReport?.id || selectedReport?.report_number}`;
    navigator.clipboard.writeText(link);
    toast.success('Lien copié dans le presse-papiers');
  };

  const shareByEmail = () => {
    const subject = encodeURIComponent(`Rapport ${selectedReport?.report_number} - ${driver?.first_name} ${driver?.last_name}`);
    const body = encodeURIComponent(`Bonjour,\n\nVeuillez trouver ci-joint le rapport journalier ${selectedReport?.report_number} du ${selectedReport?.date}.\n\nChauffeur: ${driver?.first_name} ${driver?.last_name}\nDistance: ${selectedReport?.total_km || '--'} km\nArrêts: ${selectedReport?.stops?.length || 0}\n\nCordialement`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  // Document viewing functions
  const openDocument = (doc, allDocs, index) => {
    setSelectedDocument(doc);
    setAllDocuments(allDocs);
    setCurrentDocIndex(index);
    setDocumentZoom(100);
    setShowDocumentModal(true);
  };

  const nextDocument = () => {
    if (currentDocIndex < allDocuments.length - 1) {
      setCurrentDocIndex(currentDocIndex + 1);
      setSelectedDocument(allDocuments[currentDocIndex + 1]);
      setDocumentZoom(100);
    }
  };

  const prevDocument = () => {
    if (currentDocIndex > 0) {
      setCurrentDocIndex(currentDocIndex - 1);
      setSelectedDocument(allDocuments[currentDocIndex - 1]);
      setDocumentZoom(100);
    }
  };

  const downloadDocument = (doc) => {
    const src = getDocImageSrc(doc);
    if (src) {
      const link = document.createElement('a');
      link.href = src;
      link.download = doc.filename || 'document.jpg';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Document téléchargé');
    }
  };

  const getDocumentTypeLabel = (type) => {
    const types = {
      'delivery_note': 'Bon de livraison',
      'signature': 'Signature',
      'photo': 'Photo',
      'invoice': 'Facture',
      'other': 'Autre'
    };
    return types[type] || type || 'Document';
  };

  // Resolve document image URL (handles both base64 data URLs and API paths)
  const getDocImageSrc = (doc) => {
    const url = doc?.data || doc?.image_url || '';
    if (!url) return '';
    if (url.startsWith('data:')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  // Get all documents from a report
  const getAllReportDocuments = (report) => {
    const docs = [];
    report?.stops?.forEach(stop => {
      stop.scanned_documents?.forEach(doc => {
        docs.push({
          ...doc,
          stopName: stop.client_name,
          stopType: stop.stop_type
        });
      });
    });
    return docs;
  };

  // Calculate hours for a report
  const calculateHours = (report) => {
    let totalHours = 0;
    let pauseHours = 0;

    if (report.start_time && report.end_time) {
      const startDate = report.date;
      const endDate = report.end_date || report.date;
      const start = new Date(`${startDate}T${report.start_time}`);
      let end = new Date(`${endDate}T${report.end_time}`);
      // Rollover implicite si end <= start sans end_date
      if (end <= start) {
        end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
      }
      totalHours = Math.max(0, (end - start) / (1000 * 60 * 60));
    }

    report.stops?.forEach(stop => {
      if (stop.stop_type === 'pause' && stop.arrival_datetime && stop.departure_datetime) {
        const arrival = new Date(stop.arrival_datetime);
        const departure = new Date(stop.departure_datetime);
        pauseHours += (departure - arrival) / (1000 * 60 * 60);
      }
    });

    const payableRaw = Math.max(0, totalHours - pauseHours);
    return {
      totalHours: roundToQuarterHour(totalHours),
      pauseHours: roundToQuarterHour(pauseHours),
      payableHours: roundToQuarterHour(payableRaw),
    };
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="h-8 w-48 bg-slate-800 rounded animate-pulse" />
        <div className="h-64 bg-slate-800 rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!driver) {
    return (
      <div className="p-6">
        <Link to="/admin/drivers" className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-500 mb-4">
          <ArrowLeft size={18} />
          Retour aux chauffeurs
        </Link>
        <div className="text-center py-12">
          <User className="text-slate-600 mx-auto mb-4" size={48} />
          <p className="text-slate-400">Chauffeur non trouvé</p>
        </div>
      </div>
    );
  }

  // Check if access is granted via new connection system or legacy sharing
  const hasAccess = driver.connection?.is_active || false;
  const hasLegacySharing = driver.sharing || false;
  
  const tabs = [
    { id: 'position', label: 'Position', icon: MapPin, enabled: hasAccess || driver.sharing?.share_location },
    { id: 'trips', label: 'Trajets', icon: Navigation, enabled: hasAccess },
    { id: 'reports', label: 'Rapports', icon: FileText, enabled: hasAccess || driver.sharing?.share_reports },
    { id: 'hours', label: 'Heures', icon: Clock, enabled: hasAccess || driver.sharing?.share_hours },
    { id: 'inspections', label: 'Inspections', icon: CheckCircle, enabled: hasAccess || driver.sharing?.share_inspections },
  ];

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Back Button */}
      <Link 
        to="/admin/drivers"
        className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-500 transition-colors"
      >
        <ArrowLeft size={18} />
        <span>Retour aux chauffeurs</span>
      </Link>

      {/* Driver Header */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center text-2xl font-bold text-amber-500">
              {driver.first_name?.[0]}{driver.last_name?.[0]}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {driver.first_name} {driver.last_name}
              </h1>
              <div className="mt-1 mb-1"><DriverAssignmentChip driverId={driverId} /></div>
              <p className="text-slate-400">{driver.email}</p>
              {driver.phone && (
                <p className="text-slate-500 flex items-center gap-2 mt-1">
                  <Phone size={14} />
                  {driver.phone}
                </p>
              )}
            </div>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
            driver.is_online 
              ? 'bg-green-500/20 text-green-400' 
              : 'bg-slate-800 text-slate-400'
          }`}>
            {driver.is_online ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
            {driver.is_online ? 'En ligne' : 'Hors ligne'}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-slate-900 p-1 rounded-xl overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => tab.enabled && setActiveTab(tab.id)}
            disabled={!tab.enabled}
            className={`flex-1 min-w-[120px] py-3 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeTab === tab.id
                ? 'bg-amber-500 text-black'
                : tab.enabled
                  ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                  : 'text-slate-600 cursor-not-allowed'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
            {!tab.enabled && <span className="text-xs">(non partagé)</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        {activeTab === 'position' && (hasAccess || driver.sharing?.share_location) && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <MapPin className="text-amber-500" />
              Position actuelle
            </h2>
            {driver.location ? (
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-800 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Coordonnées</p>
                  <p className="text-white text-lg font-mono">
                    {driver.location.latitude?.toFixed(6)}, {driver.location.longitude?.toFixed(6)}
                  </p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Vitesse</p>
                  <p className="text-white text-lg">
                    {driver.location.speed || 0} km/h
                  </p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Direction</p>
                  <p className="text-white text-lg flex items-center gap-2">
                    <Navigation size={16} style={{ transform: `rotate(${driver.location.heading || 0}deg)` }} />
                    {driver.location.heading || 0}°
                  </p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Dernière mise à jour</p>
                  <p className="text-white text-lg">
                    {new Date(driver.location.timestamp).toLocaleString('fr-CA')}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-slate-400">Aucune position disponible</p>
            )}
            <Link to="/admin/map">
              <Button className="bg-amber-500 hover:bg-amber-600 text-black">
                <MapPin size={16} className="mr-2" />
                Voir sur la carte
              </Button>
            </Link>
          </div>
        )}


        {activeTab === 'trips' && hasAccess && (
          <DriverTripsTab driverId={driverId} get={get} patch={patch} />
        )}


        {activeTab === 'reports' && (hasAccess || driver.sharing?.share_reports) && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <FileText className="text-amber-500" />
                Rapports journaliers
              </h2>
              <button
                onClick={() => {
                  if (!showArchived) fetchArchivedReports();
                  setShowArchived(!showArchived);
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  showArchived
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
                data-testid="toggle-archived"
              >
                {showArchived ? <Eye size={12} /> : <EyeOff size={12} />}
                {showArchived ? 'Masquer archivés' : 'Voir archivés'}
                {showArchived && archivedReports.length > 0 && (
                  <span className="bg-amber-500/30 text-amber-300 px-1.5 py-0.5 rounded-full text-[10px]">
                    {archivedReports.length}
                  </span>
                )}
              </button>
            </div>

            {/* Active Reports */}
            {reports.length > 0 ? (
              <div className="space-y-3">
                {reports.map(report => (
                  <div
                    key={report.id || report.report_number}
                    className="bg-slate-800 rounded-lg p-4 hover:bg-slate-700/50 transition-colors group"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3 cursor-pointer flex-1" onClick={() => openReport(report)}>
                        <span className="text-amber-500 font-mono font-semibold group-hover:text-amber-400">
                          {report.report_number}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          report.status === 'completed'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {report.status === 'completed' ? 'Terminé' : 'Brouillon'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-slate-400 text-sm mr-2">
                          <Calendar size={14} className="inline mr-1" />
                          {report.date}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); askConfirmation('archive', report); }}
                          className="p-1.5 rounded hover:bg-slate-600 text-slate-500 hover:text-amber-400 transition-colors"
                          title="Archiver"
                          data-testid={`archive-btn-${report.id}`}
                        >
                          <Archive size={14} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); askConfirmation('delete', report); }}
                          className="p-1.5 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400 transition-colors"
                          title="Supprimer définitivement"
                          data-testid={`delete-btn-${report.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                        <button
                          onClick={() => openReport(report)}
                          className="p-1.5 rounded hover:bg-slate-600 text-slate-500 hover:text-amber-400 transition-colors"
                          title="Voir"
                        >
                          <Eye size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm cursor-pointer" onClick={() => openReport(report)}>
                      <div>
                        <p className="text-slate-500">Début</p>
                        <p className="text-white">{report.start_time || '--:--'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Fin</p>
                        <p className="text-white">{report.end_time || '--:--'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Distance</p>
                        <p className="text-white">{report.total_km ? `${report.total_km} km` : '-- km'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Arrêts</p>
                        <p className="text-white">{report.stops?.length || 0}</p>
                      </div>
                    </div>

                    {report.stops && report.stops.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-700 cursor-pointer" onClick={() => openReport(report)}>
                        <p className="text-xs text-slate-500 mb-2">Arrêts:</p>
                        <div className="flex flex-wrap gap-2">
                          {report.stops.slice(0, 5).map((stop, idx) => (
                            <span
                              key={idx}
                              className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                                stop.stop_type === 'pickup'
                                  ? 'bg-blue-500/20 text-blue-400'
                                  : stop.stop_type === 'pause'
                                    ? 'bg-orange-500/20 text-orange-400'
                                    : 'bg-green-500/20 text-green-400'
                              }`}
                            >
                              {stop.stop_type === 'pickup' && <Package size={10} />}
                              {stop.stop_type === 'pause' && <Coffee size={10} />}
                              {stop.stop_type === 'delivery' && <Truck size={10} />}
                              {stop.client_name}
                            </span>
                          ))}
                          {report.stops.length > 5 && (
                            <span className="text-xs text-slate-500">+{report.stops.length - 5} autres</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400">Aucun rapport actif</p>
            )}

            {/* Archived Reports */}
            {showArchived && (
              <div className="space-y-3 mt-6">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                  <Archive size={14} />
                  Rapports archivés ({archivedReports.length})
                </h3>
                {archivedReports.length > 0 ? (
                  archivedReports.map(report => (
                    <div
                      key={report.id}
                      className="bg-slate-800/40 border border-slate-700/30 rounded-lg p-4 opacity-60 hover:opacity-100 transition-opacity"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 cursor-pointer flex-1" onClick={() => openReport(report)}>
                          <span className="text-slate-400 font-mono font-semibold">
                            {report.report_number}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-400">
                            Archivé
                          </span>
                          <span className="text-slate-500 text-xs">{report.date}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleUnarchive(report)}
                            className="p-1.5 rounded hover:bg-green-500/20 text-slate-500 hover:text-green-400 transition-colors"
                            title="Désarchiver"
                            data-testid={`unarchive-btn-${report.id}`}
                          >
                            <ArchiveRestore size={14} />
                          </button>
                          <button
                            onClick={() => askConfirmation('delete', report)}
                            className="p-1.5 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400 transition-colors"
                            title="Supprimer définitivement"
                          >
                            <Trash2 size={14} />
                          </button>
                          <button
                            onClick={() => openReport(report)}
                            className="p-1.5 rounded hover:bg-slate-600 text-slate-500 hover:text-slate-300 transition-colors"
                            title="Voir"
                          >
                            <Eye size={14} />
                          </button>
                        </div>
                      </div>
                      <div className="flex gap-4 text-xs text-slate-500 mt-2">
                        <span>{report.start_time || '--:--'} - {report.end_time || '--:--'}</span>
                        <span>{report.stops?.length || 0} arrêt(s)</span>
                        <span>{report.total_km ? `${report.total_km} km` : '-- km'}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 text-sm">Aucun rapport archivé</p>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'hours' && (hasAccess || driver.sharing?.share_hours) && (
          <DriverHoursTab driverId={driverId} get={get} />
        )}

        {activeTab === 'inspections' && (hasAccess || driver.sharing?.share_inspections) && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <CheckCircle className="text-amber-500" />
              Inspections véhicule
            </h2>
            <p className="text-slate-400">
              Les données d'inspection seront disponibles prochainement.
            </p>
          </div>
        )}
      </div>

      {/* Report Detail Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-4xl max-h-[90vh] overflow-hidden p-0">
          <DialogHeader className="p-4 border-b border-slate-800">
            <DialogTitle className="text-white text-lg flex items-center gap-2">
              <FileText className="text-amber-500" />
              {selectedReport?.report_number}
              <span className={`text-xs px-2 py-1 rounded ml-2 ${
                selectedReport?.status === 'completed' 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-amber-500/20 text-amber-400'
              }`}>
                {selectedReport?.status === 'completed' ? 'Terminé' : 'Brouillon'}
              </span>
              {selectedReport?.admin_edited && (
                <span
                  className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-300 flex items-center gap-1"
                  title={`Dernière correction admin : ${selectedReport?.last_admin_edit_at ? new Date(selectedReport.last_admin_edit_at).toLocaleString('fr-CA') : ''}`}
                  data-testid="admin-edited-badge"
                >
                  <Edit size={10} />
                  Modifié admin
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-auto max-h-[calc(90vh-180px)] p-4">
            {selectedReport && (
              <div className="space-y-6">
                {/* Header Info */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-slate-800 rounded-lg p-4">
                    <p className="text-slate-400 text-sm">Chauffeur</p>
                    <p className="text-white text-lg font-semibold">{driver?.first_name} {driver?.last_name}</p>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-4" data-testid="report-modal-vehicle">
                    <p className="text-slate-400 text-sm">Véhicule</p>
                    <p className="text-white text-lg font-semibold">{selectedReport.vehicle_label || '--'}</p>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-4">
                    <p className="text-slate-400 text-sm">Date</p>
                    <p className="text-white text-lg font-semibold">{selectedReport.date}</p>
                  </div>
                </div>

                {/* Time & Distance */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-800 rounded-lg p-4 text-center">
                    <p className="text-slate-400 text-sm">Début</p>
                    <p className="text-white text-xl font-bold">{selectedReport.start_time || '--:--'}</p>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-4 text-center">
                    <p className="text-slate-400 text-sm">Fin</p>
                    <p className="text-white text-xl font-bold">{selectedReport.end_time || '--:--'}</p>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-4 text-center">
                    <p className="text-slate-400 text-sm">Heures payables</p>
                    <p className="text-amber-500 text-xl font-bold">
                      {formatHoursDecimal(calculateHours(selectedReport).payableHours)}
                    </p>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-4 text-center">
                    <p className="text-slate-400 text-sm">Distance</p>
                    <p className="text-white text-xl font-bold">{selectedReport.total_km || '--'} km</p>
                  </div>
                </div>

                {/* Sous-traitant */}
                {selectedReport.subcontractor && (
                  <div className="bg-slate-800 rounded-lg p-4">
                    <p className="text-slate-400 text-sm">Sous-traitant</p>
                    <p className="text-white text-lg">{selectedReport.subcontractor}</p>
                  </div>
                )}

                {/* Stops */}
                <div>
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <MapPin className="text-amber-500" size={18} />
                    Arrêts ({selectedReport.stops?.length || 0})
                  </h3>
                  <div className="space-y-3">
                    {selectedReport.stops?.sort((a, b) => a.order - b.order).map((stop, index) => (
                      <div 
                        key={stop.id || index}
                        className="bg-slate-800 rounded-lg p-4"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                            stop.stop_type === 'pickup' 
                              ? 'bg-blue-500/20 text-blue-400'
                              : stop.stop_type === 'pause'
                                ? 'bg-orange-500/20 text-orange-400'
                                : 'bg-green-500/20 text-green-400'
                          }`}>
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-white">{stop.client_name}</h4>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                stop.stop_type === 'pickup' 
                                  ? 'bg-blue-500/20 text-blue-400'
                                  : stop.stop_type === 'pause'
                                    ? 'bg-orange-500/20 text-orange-400'
                                    : 'bg-green-500/20 text-green-400'
                              }`}>
                                {stop.stop_type === 'pickup' ? 'Cueillette' : 
                                 stop.stop_type === 'pause' ? 'Pause' : 'Livraison'}
                              </span>
                            </div>
                            
                            {stop.address && (
                              <p className="text-slate-400 text-sm mb-2">
                                <MapPin size={12} className="inline mr-1" />
                                {stop.address}
                              </p>
                            )}
                            
                            {/* Pause details */}
                            {stop.stop_type === 'pause' && stop.pause_type && (
                              <p className="text-orange-400 text-sm mb-2">
                                {stop.pause_type === 'lunch' ? 'Dîner' :
                                 stop.pause_type === 'dinner' ? 'Souper' :
                                 stop.pause_type === 'break' ? 'Pause café' :
                                 stop.pause_type === 'overnight' ? 'Nuit / Repos' : 'Autre'}
                              </p>
                            )}
                            
                            {/* Quantities for non-pause stops */}
                            {stop.stop_type !== 'pause' && stop.quantities && (
                              <div className="flex flex-wrap gap-2 text-xs mb-2">
                                {stop.quantities.palettes > 0 && (
                                  <span className="bg-slate-700 px-2 py-1 rounded text-slate-300">
                                    {stop.quantities.palettes} palettes
                                  </span>
                                )}
                                {stop.quantities.units > 0 && (
                                  <span className="bg-slate-700 px-2 py-1 rounded text-slate-300">
                                    {stop.quantities.units} unités
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {/* Delivery notes */}
                            {stop.delivery_note_numbers?.length > 0 && (
                              <p className="text-slate-400 text-sm mb-2">
                                <strong>Bons:</strong> {stop.delivery_note_numbers.join(', ')}
                              </p>
                            )}
                            
                            {/* Times */}
                            <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                              {stop.arrival_datetime && (
                                <span>
                                  <Clock size={12} className="inline mr-1" />
                                  Arrivée: {new Date(stop.arrival_datetime).toLocaleString('fr-CA')}
                                </span>
                              )}
                              {stop.departure_datetime && (
                                <span>
                                  Départ: {new Date(stop.departure_datetime).toLocaleString('fr-CA')}
                                </span>
                              )}
                            </div>
                            
                            {stop.notes && (
                              <div className="mt-2 text-sm bg-amber-500/10 border-l-2 border-amber-500/60 rounded-r px-3 py-2" data-testid={`report-modal-stop-notes-${index}`}>
                                <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-400">Notes / Remarques</span>
                                <p className="text-amber-100 whitespace-pre-wrap mt-0.5">{stop.notes}</p>
                              </div>
                            )}
                            
                            {/* Scanned Documents */}
                            {stop.scanned_documents && stop.scanned_documents.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-slate-700">
                                <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                                  <FileImage size={12} />
                                  Documents scannés ({stop.scanned_documents.length})
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {stop.scanned_documents.map((doc, docIndex) => (
                                    <button
                                      key={doc.id || docIndex}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDocument(doc, stop.scanned_documents, docIndex);
                                      }}
                                      className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 px-3 py-2 rounded text-xs text-slate-300 transition-colors"
                                    >
                                      <FileImage size={14} className="text-amber-400" />
                                      <span className="max-w-[150px] truncate">{doc.filename || `Document ${docIndex + 1}`}</span>
                                      <Eye size={12} className="text-slate-400" />
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* All Documents Summary */}
                {getAllReportDocuments(selectedReport).length > 0 && (
                  <div className="bg-slate-800 rounded-lg p-4">
                    <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <FileImage className="text-amber-500" size={18} />
                      Tous les documents ({getAllReportDocuments(selectedReport).length})
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {getAllReportDocuments(selectedReport).map((doc, idx) => (
                        <button
                          key={doc.id || idx}
                          onClick={() => openDocument(doc, getAllReportDocuments(selectedReport), idx)}
                          className="bg-slate-700 hover:bg-slate-600 rounded-lg p-3 text-left transition-colors group"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <FileImage size={16} className="text-amber-400" />
                            <span className="text-xs text-slate-400">{getDocumentTypeLabel(doc.type)}</span>
                          </div>
                          <p className="text-sm text-white truncate group-hover:text-amber-400">
                            {doc.filename || `Document ${idx + 1}`}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">{doc.stopName}</p>
                        </button>
                      ))}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-slate-600 text-xs"
                        onClick={() => {
                          const docs = getAllReportDocuments(selectedReport);
                          docs.forEach(doc => downloadDocument(doc));
                        }}
                      >
                        <Download size={14} className="mr-1" />
                        Tout télécharger
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="p-4 border-t border-slate-800 flex-row gap-2">
            <Button 
              variant="outline" 
              className="border-blue-700 text-blue-300 hover:bg-blue-900/30 flex-1"
              onClick={() => setShowEditModal(true)}
              data-testid="open-edit-report-btn"
            >
              <Edit size={16} className="mr-2" />
              Éditer
            </Button>
            <Button 
              variant="outline" 
              className="border-slate-700 flex-1"
              onClick={() => setShowShareModal(true)}
            >
              <Share2 size={16} className="mr-2" />
              Partager
            </Button>
            <Button 
              className="bg-amber-500 hover:bg-amber-600 text-black flex-1"
              onClick={printReport}
            >
              <Printer size={16} className="mr-2" />
              Imprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Edit Modal (correction post-completion) */}
      <AdminReportEditModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        driverId={driverId}
        report={selectedReport}
        onSaved={async () => {
          // Rafraîchir la liste des rapports et le rapport sélectionné
          const res = await get(`/saas/driver/${driverId}/reports`);
          if (res.success) {
            setReports(res.data);
            if (selectedReport?.id) {
              const refreshed = res.data.find(r => r.id === selectedReport.id);
              if (refreshed) setSelectedReport(refreshed);
            }
          }
        }}
      />

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Share2 className="text-amber-500" />
              Partager le rapport
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-slate-400 text-sm">
              Rapport: <span className="text-amber-500 font-mono">{selectedReport?.report_number}</span>
            </p>
            
            <div className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full border-slate-700 justify-start"
                onClick={shareByEmail}
              >
                <Mail size={16} className="mr-3 text-blue-400" />
                Envoyer par email
              </Button>
              
              <Button 
                variant="outline" 
                className="w-full border-slate-700 justify-start"
                onClick={copyReportLink}
              >
                <Copy size={16} className="mr-3 text-green-400" />
                Copier le lien
              </Button>
              
              <Button 
                variant="outline" 
                className="w-full border-slate-700 justify-start"
                onClick={printReport}
              >
                <Printer size={16} className="mr-3 text-amber-400" />
                Imprimer / PDF
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="border-slate-700">
                Fermer
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Viewer Modal */}
      <Dialog open={showDocumentModal} onOpenChange={setShowDocumentModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-5xl max-h-[95vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b border-slate-800 flex-row items-center justify-between">
            <DialogTitle className="text-white text-lg flex items-center gap-2">
              <FileImage className="text-amber-500" />
              {selectedDocument?.filename || 'Document'}
              <span className="text-xs bg-slate-700 px-2 py-1 rounded text-slate-300 ml-2">
                {getDocumentTypeLabel(selectedDocument?.type)}
              </span>
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDocumentZoom(Math.max(25, documentZoom - 25))}
                className="h-8 w-8 p-0 border-slate-700"
              >
                <ZoomOut size={16} />
              </Button>
              <span className="text-slate-400 text-sm w-14 text-center">{documentZoom}%</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDocumentZoom(Math.min(200, documentZoom + 25))}
                className="h-8 w-8 p-0 border-slate-700"
              >
                <ZoomIn size={16} />
              </Button>
            </div>
          </DialogHeader>
          
          {/* Document Image */}
          <div className="relative bg-slate-950 min-h-[400px] max-h-[calc(95vh-200px)] overflow-auto flex items-center justify-center p-4">
            {getDocImageSrc(selectedDocument) ? (
              <img 
                src={getDocImageSrc(selectedDocument)}
                alt={selectedDocument?.filename || 'Document'}
                style={{ 
                  maxWidth: `${documentZoom}%`,
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
                className="rounded shadow-lg"
              />
            ) : (
              <div className="text-center text-slate-400">
                <FileImage size={64} className="mx-auto mb-4 opacity-50" />
                <p>Aperçu non disponible</p>
              </div>
            )}
            
            {/* Navigation arrows */}
            {allDocuments.length > 1 && (
              <>
                <button
                  onClick={prevDocument}
                  disabled={currentDocIndex === 0}
                  className={`absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    currentDocIndex === 0 
                      ? 'bg-slate-800 text-slate-600 cursor-not-allowed'
                      : 'bg-slate-700 hover:bg-amber-500 text-white'
                  }`}
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={nextDocument}
                  disabled={currentDocIndex === allDocuments.length - 1}
                  className={`absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    currentDocIndex === allDocuments.length - 1
                      ? 'bg-slate-800 text-slate-600 cursor-not-allowed'
                      : 'bg-slate-700 hover:bg-amber-500 text-white'
                  }`}
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}
          </div>
          
          {/* Document Info */}
          <div className="p-4 border-t border-slate-800 bg-slate-900">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-white font-semibold">{selectedDocument?.filename}</p>
                <p className="text-slate-400 text-sm">
                  {selectedDocument?.stopName && `Arrêt: ${selectedDocument.stopName}`}
                  {selectedDocument?.uploaded_at && ` • ${new Date(selectedDocument.uploaded_at).toLocaleString('fr-CA')}`}
                </p>
              </div>
              {allDocuments.length > 1 && (
                <span className="text-slate-500 text-sm">
                  {currentDocIndex + 1} / {allDocuments.length}
                </span>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="border-slate-700 flex-1"
                onClick={() => downloadDocument(selectedDocument)}
              >
                <Download size={16} className="mr-2" />
                Télécharger
              </Button>
              <Button
                variant="outline"
                className="border-slate-700 flex-1"
                onClick={() => {
                  if (selectedDocument?.data) {
                    window.open(selectedDocument.data, '_blank');
                  }
                }}
              >
                <ExternalLink size={16} className="mr-2" />
                Ouvrir
              </Button>
              <Button
                className="bg-amber-500 hover:bg-amber-600 text-black flex-1"
                onClick={() => {
                  const link = selectedDocument?.data || '';
                  navigator.clipboard.writeText(link);
                  toast.success('Lien du document copié');
                }}
              >
                <Share2 size={16} className="mr-2" />
                Partager
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Archive/Delete */}
      <Dialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {confirmAction?.type === 'delete' ? (
                <><Trash2 className="text-red-500" size={20} /> Supprimer définitivement</>
              ) : (
                <><Archive className="text-amber-500" size={20} /> Archiver le rapport</>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <p className="text-slate-300 text-sm">
              Rapport: <span className="text-amber-500 font-mono font-bold">{confirmAction?.report?.report_number}</span>
              <span className="text-slate-500 ml-2">({confirmAction?.report?.date})</span>
            </p>

            {!reportHasInvoice(confirmAction?.report || {}) && (
              <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={16} />
                <p className="text-amber-300 text-sm">
                  Ce rapport n'a pas été facturé. Êtes-vous certain de vouloir {confirmAction?.type === 'delete' ? 'le supprimer définitivement' : 'l\'archiver maintenant'}?
                </p>
              </div>
            )}

            {confirmAction?.type === 'delete' && (
              <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 flex items-start gap-2">
                <Trash2 className="text-red-500 shrink-0 mt-0.5" size={16} />
                <p className="text-red-300 text-sm">
                  Cette action est irréversible. Le rapport, ses documents scannés et ses heures de service associées seront supprimés.
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="flex-row gap-2">
            <Button
              variant="outline"
              className="flex-1 border-slate-700"
              onClick={() => setConfirmAction(null)}
            >
              Non
            </Button>
            <Button
              className={`flex-1 ${confirmAction?.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'} text-white`}
              onClick={() => {
                if (confirmAction?.type === 'delete') handlePermanentDelete(confirmAction.report);
                else handleArchive(confirmAction.report);
              }}
            >
              Oui, {confirmAction?.type === 'delete' ? 'supprimer' : 'archiver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== DRIVER TRIPS TAB ====================
// Vue admin alignee sur l'appli chauffeur (RouteSheetPage.js)
// Affiche les rapports journaliers (RJ-...) sous forme de cartes avec
// resume arrets/km/duree + timeline des arrets.

const tripStatusBadge = {
  draft:       { label: 'Brouillon', cls: 'bg-slate-700/50 text-slate-300' },
  in_progress: { label: 'En cours',  cls: 'bg-amber-500/20 text-amber-300' },
  completed:   { label: 'Termine',   cls: 'bg-emerald-500/20 text-emerald-300' },
};

const tripStopMeta = {
  pickup:     { label: 'Cueillette', icon: Package, color: 'text-blue-400',    dot: 'bg-blue-400' },
  delivery:   { label: 'Livraison',  icon: MapPin,  color: 'text-emerald-400', dot: 'bg-emerald-400' },
  pause:      { label: 'Pause',      icon: Coffee,  color: 'text-amber-400',   dot: 'bg-amber-400' },
  fin_trajet: { label: 'Fin trajet', icon: Flag,    color: 'text-slate-400',   dot: 'bg-slate-400' },
};

const AdminReportCard = ({ report, onOpenMap }) => {
  const sb = tripStatusBadge[report.status] || tripStatusBadge.draft;
  const stops = Array.isArray(report.stops) ? report.stops : [];
  const dateLabel = report.end_date && report.end_date !== report.date
    ? `${formatShortDate(report.date)} → ${formatShortDate(report.end_date)}`
    : formatShortDate(report.date);

  // Calcule les heures PAYABLES (total - pauses & fin_trajet), meme logique que
  // DailyReportPage. Cote admin on veut voir le montant a payer/facturer, pas le brut.
  // Format: report.date="2026-09-17", report.start_time="08:06" (pas des datetime complets)
  const payableHours = (() => {
    let totalH = 0;
    if (report.start_time && report.date) {
      const startDate = report.date;
      const endDate = report.end_date || report.date;
      const start = new Date(`${startDate}T${report.start_time}`);
      const end = report.end_time
        ? new Date(`${endDate}T${report.end_time}`)
        : new Date(); // trajet en cours
      if (!isNaN(start) && !isNaN(end) && end > start) {
        totalH = (end - start) / 3600000;
      }
    }
    let pauseH = 0;
    for (const s of stops) {
      if ((s.stop_type === 'pause' || s.stop_type === 'fin_trajet')
          && s.arrival_datetime && s.departure_datetime) {
        const a = new Date(s.arrival_datetime);
        const d = new Date(s.departure_datetime);
        if (!isNaN(a) && !isNaN(d) && d > a) {
          pauseH += (d - a) / 3600000;
        }
      }
    }
    const raw = Math.max(0, totalH - pauseH);
    return raw > 0 ? roundToQuarterHour(raw) : null;
  })();

  return (
    <div
      className="block w-full text-left bg-slate-900 border border-slate-800 rounded-xl overflow-hidden"
      data-testid={`admin-report-card-${report.report_number}`}
    >
      {/* Header — le cercle amber ouvre la carte GPS */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-950/50">
        <button
          type="button"
          onClick={onOpenMap}
          className="flex items-center gap-2.5 min-w-0 flex-1 group active:scale-95 transition-transform"
          data-testid={`admin-open-map-${report.report_number}`}
          title="Voir le trajet sur la carte"
        >
          <span className="w-9 h-9 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center shrink-0 group-hover:bg-amber-500/25 group-active:bg-amber-500/30">
            <RouteIcon size={18} className="text-amber-400" />
          </span>
          <div className="min-w-0 text-left">
            <h3 className="text-white font-bold text-base truncate">{report.report_number}</h3>
            <p className="text-slate-400 text-xs">{dateLabel}</p>
          </div>
        </button>
        <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full ${sb.cls}`}>
          {sb.label}
        </span>
      </div>

      {/* Stats row — cote admin on affiche les heures PAYABLES */}
      <div className="grid grid-cols-3 px-4 py-3 border-b border-slate-800 text-center">
        <div>
          <p className="text-amber-400 font-bold text-lg leading-tight">{stops.length}</p>
          <p className="text-slate-500 text-[10px] uppercase tracking-wider mt-0.5">Arrets</p>
        </div>
        <div className="border-x border-slate-800">
          <p className="text-amber-400 font-bold text-lg leading-tight">
            {report.total_km != null ? `${report.total_km}` : '—'}
            <span className="text-xs text-slate-500 ml-1">km</span>
          </p>
          <p className="text-slate-500 text-[10px] uppercase tracking-wider mt-0.5">Distance</p>
        </div>
        <div>
          <p className="text-amber-400 font-bold text-lg leading-tight" title={payableHours != null ? formatHoursHuman(payableHours) : ''}>
            {payableHours != null ? formatHoursHuman(payableHours) : '—'}
          </p>
          <p className="text-slate-500 text-[10px] uppercase tracking-wider mt-0.5">Payable</p>
        </div>
      </div>

      {/* Stops timeline */}
      {stops.length > 0 ? (
        <div className="px-4 py-3">
          <ol className="relative ml-1">
            {stops.map((stop, i) => {
              const meta = tripStopMeta[stop.stop_type] || tripStopMeta.delivery;
              const Icon = meta.icon;
              const isLast = i === stops.length - 1;
              return (
                <li key={stop.id || i} className="relative flex gap-3 pb-3 last:pb-0">
                  {!isLast && (
                    <span className="absolute left-[11px] top-6 bottom-0 w-px bg-slate-700" aria-hidden="true" />
                  )}
                  <div className={`w-6 h-6 rounded-full ${meta.dot} bg-opacity-20 flex items-center justify-center shrink-0 z-10`}>
                    <Icon size={12} className={meta.color} />
                  </div>
                  <div className="flex-1 min-w-0 -mt-0.5">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-wider font-semibold ${meta.color}`}>
                        {meta.label}
                      </span>
                      <p className="text-white text-sm font-medium truncate flex-1">
                        {stop.client_name || stop.address || '—'}
                      </p>
                    </div>
                    {stop.arrival_datetime && (
                      <p className="text-slate-400 text-xs tabular-nums mt-0.5">
                        {formatStopDateTime(stop.arrival_datetime)}
                        {stop.departure_datetime && (
                          <span className="text-slate-500"> → {formatStopDateTime(stop.departure_datetime)}</span>
                        )}
                      </p>
                    )}
                    {stop.notes && (
                      <p
                        className="mt-1 text-xs text-amber-200/90 bg-amber-500/10 border-l-2 border-amber-500/60 rounded-r px-2 py-1 whitespace-pre-wrap"
                        data-testid={`admin-report-stop-notes-${stop.id || i}`}
                      >
                        {stop.notes}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      ) : (
        <div className="px-4 py-3 text-slate-500 text-xs italic">
          Aucun arret enregistre
        </div>
      )}
    </div>
  );
};

const DriverTripsTab = ({ driverId, get }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // active = draft+in_progress, completed
  const [mapReport, setMapReport] = useState(null); // { id, report_number } when open

  const fetchReports = async () => {
    setLoading(true);
    try {
      const result = await get(`/saas/driver/${driverId}/reports`);
      const list = Array.isArray(result?.data) ? result.data : (Array.isArray(result) ? result : []);
      // Sort by date desc, then start_time desc
      list.sort((a, b) => {
        const dateCmp = (b.date || '').localeCompare(a.date || '');
        if (dateCmp !== 0) return dateCmp;
        return (b.start_time || '').localeCompare(a.start_time || '');
      });
      setReports(list);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, [driverId]);

  const visibleReports = reports.filter(r => {
    if (filter === 'active') return r.status !== 'completed';
    return r.status === 'completed';
  });

  return (
    <div className="space-y-4" data-testid="driver-trips-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Navigation className="text-amber-500" />
          Mes trajets
        </h2>
        <div className="flex gap-2">
          {[
            { key: 'active', label: 'En cours / Brouillons' },
            { key: 'completed', label: 'Termines' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filter === f.key ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
              data-testid={`admin-trips-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
          <button onClick={fetchReports} className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700" data-testid="admin-trips-refresh">
            <RefreshCw size={14} className="text-slate-400" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="bg-slate-900 rounded-xl p-4 animate-pulse h-40">
              <div className="h-4 bg-slate-800 rounded w-1/3 mb-3" />
              <div className="h-4 bg-slate-800 rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : visibleReports.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Truck className="mx-auto mb-2" size={32} />
          <p className="text-sm">
            {filter === 'active' ? 'Aucun trajet en cours' : 'Aucun trajet termine'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleReports.map(r => (
            <AdminReportCard
              key={r.id}
              report={r}
              onOpenMap={() => setMapReport({ id: r.id, report_number: r.report_number })}
            />
          ))}
        </div>
      )}

      {mapReport && (
        <ReportGpsMapModal
          open={!!mapReport}
          onClose={() => setMapReport(null)}
          reportId={mapReport.id}
          reportNumber={mapReport.report_number}
        />
      )}
    </div>
  );
};


// ==================== DRIVER HOURS TAB ====================

const LIMITS = {
  maxDriving: 13 * 60,
  maxOnDuty: 14 * 60,
  cycle1: { days: 7, maxHours: 70 * 60, resetHours: 36 },
  cycle2: { days: 14, maxHours: 120 * 60, resetHours: 72 }
};

const fmtDur = (min) => {
  const h = Math.floor(Math.abs(min) / 60);
  const m = Math.abs(min) % 60;
  return `${h}h ${String(m).padStart(2, '0')}min`;
};

const DriverHoursTab = ({ driverId, get }) => {
  const [todaySummary, setTodaySummary] = useState(null);
  const [cycleData, setCycleData] = useState(null);
  const [selectedCycle, setSelectedCycle] = useState('cycle1');
  const [showCycleSelector, setShowCycleSelector] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, cycleRes] = await Promise.all([
        get(`/saas/driver/${driverId}/service-hours/summary?date=${today}`),
        get(`/saas/driver/${driverId}/service-hours/cycle?cycle_days=${selectedCycle === 'cycle1' ? 7 : 14}`)
      ]);
      if (summaryRes.success) setTodaySummary(summaryRes.data);
      if (cycleRes.success) setCycleData(cycleRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resyncHours = async () => {
    setSyncing(true);
    try {
      const res = await get(`/../admin/resync-service-hours/${driverId}`);
      if (res.success) {
        toast.success(`Re-sync terminé: ${res.data.reports_processed} rapports traités, ${res.data.new_entries_created} entrées créées`);
        fetchData();
      } else {
        // Try with POST via raw fetch
        const token = localStorage.getItem('admin_token');
        const API = process.env.REACT_APP_BACKEND_URL;
        const r = await fetch(`${API}/api/admin/resync-service-hours/${driverId}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        const data = await r.json();
        if (data.success) {
          toast.success(`Re-sync terminé: ${data.reports_processed} rapports traités, ${data.new_entries_created} entrées créées`);
          fetchData();
        } else {
          toast.error('Erreur lors de la re-synchronisation');
        }
      }
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la re-synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { fetchData(); }, [driverId, selectedCycle]);

  const pct = (val, max) => Math.min((val / max) * 100, 100);
  const isOver = (val, max) => val >= max;

  if (loading) {
    return (
      <div className="space-y-4" data-testid="hours-tab-loading">
        <div className="h-32 bg-slate-800 rounded-xl animate-pulse" />
        <div className="h-48 bg-slate-800 rounded-xl animate-pulse" />
      </div>
    );
  }

  const drivingMin = todaySummary?.driving_min || 0;
  const onDutyMin = todaySummary?.on_duty_min || 0;
  const totalWorkMin = drivingMin + onDutyMin;

  return (
    <div className="space-y-5" data-testid="driver-hours-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Clock className="text-amber-500" />
          Heures de service
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={resyncHours}
            disabled={syncing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/30 transition-colors disabled:opacity-50"
            data-testid="hours-resync"
            title="Recalculer les heures depuis les rapports"
          >
            <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Sync...' : 'Re-sync'}
          </button>
          <button
            onClick={fetchData}
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700"
            data-testid="hours-refresh"
          >
            <RefreshCw size={14} className="text-slate-400" />
          </button>
        </div>
      </div>

      {/* Today's Summary */}
      <div className="bg-slate-800/50 rounded-xl p-4" data-testid="hours-today-summary">
        <p className="text-xs text-slate-500 uppercase font-bold tracking-wider mb-4">
          Résumé du jour — {new Date().toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>

        {/* Driving */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
              <span className="text-slate-300 text-sm font-medium">Conduite</span>
            </div>
            <span className={`font-mono text-sm font-bold ${isOver(drivingMin, LIMITS.maxDriving) ? 'text-red-500' : 'text-white'}`}>
              {fmtDur(drivingMin)} / {fmtDur(LIMITS.maxDriving)}
            </span>
          </div>
          <div className="h-2.5 bg-slate-700 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${isOver(drivingMin, LIMITS.maxDriving) ? 'bg-red-500' : 'bg-amber-500'}`}
              style={{ width: `${pct(drivingMin, LIMITS.maxDriving)}%` }}
            />
          </div>
        </div>

        {/* On Duty / Total service */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
              <span className="text-slate-300 text-sm font-medium">Total service</span>
            </div>
            <span className={`font-mono text-sm font-bold ${isOver(totalWorkMin, LIMITS.maxOnDuty) ? 'text-red-500' : 'text-white'}`}>
              {fmtDur(totalWorkMin)} / {fmtDur(LIMITS.maxOnDuty)}
            </span>
          </div>
          <div className="h-2.5 bg-slate-700 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${isOver(totalWorkMin, LIMITS.maxOnDuty) ? 'bg-red-500' : 'bg-blue-500'}`}
              style={{ width: `${pct(totalWorkMin, LIMITS.maxOnDuty)}%` }}
            />
          </div>
        </div>

        {/* Visual Timeline Bar */}
        <div className="h-8 bg-slate-700 rounded-lg overflow-hidden flex mt-4">
          {drivingMin > 0 && (
            <div
              className="bg-amber-500 flex items-center justify-center text-xs text-black font-bold"
              style={{ width: `${(drivingMin / LIMITS.maxOnDuty) * 100}%` }}
            >
              {drivingMin > 30 && `${Math.floor(drivingMin / 60)}h`}
            </div>
          )}
          {onDutyMin > 0 && (
            <div
              className="bg-blue-500 flex items-center justify-center text-xs text-white font-bold"
              style={{ width: `${(onDutyMin / LIMITS.maxOnDuty) * 100}%` }}
            >
              {onDutyMin > 30 && `${Math.floor(onDutyMin / 60)}h`}
            </div>
          )}
          {(todaySummary?.off_duty_min || 0) > 0 && (
            <div
              className="bg-slate-600 flex items-center justify-center text-xs text-slate-300 font-bold"
              style={{ width: `${((todaySummary?.off_duty_min || 0) / LIMITS.maxOnDuty) * 100}%` }}
            />
          )}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-3">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
            <span className="text-xs text-slate-400">Conduite</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
            <span className="text-xs text-slate-400">En service</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-slate-600" />
            <span className="text-xs text-slate-400">Repos</span>
          </div>
        </div>

        {/* Today's entries detail */}
        {todaySummary?.entries?.length > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 mb-2">Segments du jour</p>
            <div className="space-y-1">
              {todaySummary.entries.map((e, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs py-1">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${
                      e.status === 'driving' ? 'bg-amber-500' :
                      e.status === 'on_duty' ? 'bg-blue-500' :
                      e.status === 'sleeper' ? 'bg-purple-500' : 'bg-slate-600'
                    }`} />
                    <span className="text-slate-300">
                      {e.status === 'driving' ? 'Conduite' :
                       e.status === 'on_duty' ? 'En service' :
                       e.status === 'sleeper' ? 'Couchette' : 'Repos'}
                    </span>
                  </div>
                  <span className="text-slate-400 font-mono">
                    {e.start_time} - {e.end_time || '...'} ({e.duration_min ? fmtDur(e.duration_min) : '--'})
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* SAAQ Cycle Recap */}
      <div className="bg-slate-800/50 rounded-xl p-4" data-testid="hours-cycle-recap">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="text-green-500" size={18} />
            <span className="text-xs text-slate-500 uppercase font-bold tracking-wider">Récap cycle SAAQ</span>
          </div>
          <div className="relative">
            <button
              onClick={() => setShowCycleSelector(!showCycleSelector)}
              className="flex items-center gap-2 bg-slate-700 px-3 py-1.5 rounded-lg text-xs text-white hover:bg-slate-600 transition-colors"
              data-testid="admin-cycle-selector"
            >
              {selectedCycle === 'cycle1' ? 'Cycle 1 (7 jours)' : 'Cycle 2 (14 jours)'}
              <ChevronDown size={14} className={`transition-transform ${showCycleSelector ? 'rotate-180' : ''}`} />
            </button>
            {showCycleSelector && (
              <div className="absolute right-0 top-full mt-1 z-10 w-56 bg-slate-700 rounded-lg shadow-xl border border-slate-600 overflow-hidden">
                <button
                  onClick={() => { setSelectedCycle('cycle1'); setShowCycleSelector(false); }}
                  className={`w-full p-3 text-left text-xs hover:bg-slate-600 ${selectedCycle === 'cycle1' ? 'bg-green-600/30' : ''}`}
                >
                  <p className="font-bold text-white">Cycle 1 — 7 jours</p>
                  <p className="text-slate-400 mt-0.5">Max 70h | Reset: 36h repos</p>
                </button>
                <button
                  onClick={() => { setSelectedCycle('cycle2'); setShowCycleSelector(false); }}
                  className={`w-full p-3 text-left text-xs hover:bg-slate-600 ${selectedCycle === 'cycle2' ? 'bg-green-600/30' : ''}`}
                >
                  <p className="font-bold text-white">Cycle 2 — 14 jours</p>
                  <p className="text-slate-400 mt-0.5">Max 120h | Reset: 72h repos</p>
                </button>
              </div>
            )}
          </div>
        </div>

        {cycleData ? (
          <>
            {/* Cycle Totals */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-slate-900/60 rounded-lg p-3 text-center">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Heures travaillées</p>
                <p className="text-xl font-bold text-white font-mono mt-1">
                  {fmtDur(cycleData.total_work_min)}
                </p>
                <p className="text-[10px] text-slate-400">
                  sur {cycleData.cycle_days} jours
                </p>
              </div>
              <div className="bg-slate-900/60 rounded-lg p-3 text-center">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Heures restantes</p>
                <p className={`text-xl font-bold font-mono mt-1 ${
                  cycleData.remaining_min < 5 * 60 ? 'text-red-500' :
                  cycleData.remaining_min < 10 * 60 ? 'text-amber-500' : 'text-green-500'
                }`}>
                  {fmtDur(cycleData.remaining_min)}
                </p>
                <p className="text-[10px] text-slate-400">
                  max {fmtDur(cycleData.max_hours_min)}
                </p>
              </div>
            </div>

            {/* Cycle Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-slate-400 text-xs">Utilisation du cycle</span>
                <span className="text-xs font-mono text-white font-bold">{cycleData.utilization_pct}%</span>
              </div>
              <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    cycleData.utilization_pct >= 90 ? 'bg-red-500' :
                    cycleData.utilization_pct >= 75 ? 'bg-amber-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(cycleData.utilization_pct, 100)}%` }}
                />
              </div>
            </div>

            {/* Daily Breakdown */}
            {cycleData.daily_data?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-700">
                <p className="text-xs text-slate-500 font-bold uppercase mb-2">Détail par jour</p>
                <div className="space-y-1.5">
                  {cycleData.daily_data.filter(d => d.work_min > 0).map(d => {
                    const dateObj = new Date(d.date + 'T12:00:00');
                    const label = dateObj.toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' });
                    return (
                      <div key={d.date} className="flex items-center justify-between py-1.5 text-xs">
                        <span className="text-slate-400 w-32">{label}</span>
                        <div className="flex-1 mx-3 h-2 bg-slate-700 rounded-full overflow-hidden flex">
                          {d.driving_min > 0 && (
                            <div className="bg-amber-500 h-full" style={{ width: `${(d.driving_min / (14 * 60)) * 100}%` }} />
                          )}
                          {d.on_duty_min > 0 && (
                            <div className="bg-blue-500 h-full" style={{ width: `${(d.on_duty_min / (14 * 60)) * 100}%` }} />
                          )}
                        </div>
                        <span className="text-white font-mono font-medium w-24 text-right">{fmtDur(d.work_min)}</span>
                      </div>
                    );
                  })}
                  {cycleData.daily_data.filter(d => d.work_min > 0).length === 0 && (
                    <p className="text-slate-500 text-xs text-center py-2">Aucune activité dans ce cycle</p>
                  )}
                </div>
              </div>
            )}

            {/* Reset Info */}
            <div className="flex items-center gap-2 p-2.5 bg-slate-900/40 rounded-lg mt-3">
              <RefreshCw className="text-blue-400 shrink-0" size={14} />
              <p className="text-xs text-slate-400">
                Remise à zéro: <span className="text-white font-medium">{cycleData.reset_hours}h de repos consécutives</span>
              </p>
            </div>
          </>
        ) : (
          <div className="text-center py-6">
            <div className="w-5 h-5 border-2 border-green-500/30 border-t-green-500 rounded-full animate-spin mx-auto" />
            <p className="text-slate-400 text-xs mt-2">Chargement du cycle...</p>
          </div>
        )}
      </div>

      {/* Warning if close to limits */}
      {(drivingMin >= LIMITS.maxDriving * 0.9 || totalWorkMin >= LIMITS.maxOnDuty * 0.9) && (
        <div className="bg-amber-900/30 border border-amber-800 rounded-xl p-3 flex items-start gap-2" data-testid="hours-daily-warning">
          <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={16} />
          <div>
            <p className="text-amber-400 font-bold text-sm">Limite journalière proche</p>
            <p className="text-amber-300 text-xs mt-0.5">Ce chauffeur approche la limite légale d'heures de service.</p>
          </div>
        </div>
      )}

      {cycleData && cycleData.remaining_min < 10 * 60 && (
        <div className="bg-red-900/30 border border-red-800 rounded-xl p-3 flex items-start gap-2" data-testid="hours-cycle-warning">
          <AlertTriangle className="text-red-500 shrink-0 mt-0.5" size={16} />
          <div>
            <p className="text-red-400 font-bold text-sm">Limite de cycle proche!</p>
            <p className="text-red-300 text-xs mt-0.5">
              Moins de 10h restantes sur le {selectedCycle === 'cycle1' ? 'cycle de 7 jours' : 'cycle de 14 jours'}.
              {cycleData.reset_hours}h de repos consécutives requises pour remise à zéro.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};


export default DriverDetailPage;
